import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { InlineDatePickerField, InlineDatePickerPanel } from "../DatePickerField";
import InvoiceDocumentEditor from "./InvoiceDocumentEditor";
import PacketActions from "./PacketActions";
import {
  addPartialDeposit,
  formatDisplayDate,
  formatUsd,
  isPartialPlan,
  normalizePaymentPlan,
  PAYMENT_PLAN_LABELS,
  reconcileDepositSchedule,
  removePartialDeposit,
  roundMoney,
  syncDepositsToPaymentPlan,
} from "../../data/revenueData";
import { deleteRevenueInvoice, patchRevenueInvoice } from "../../lib/revenueApi";
import { formatApiDetail } from "../../lib/api";

const SIDE_CONTROL =
  "t-input !rounded-[var(--t-event-pill-radius)] !bg-white w-full min-h-[40px] h-10 text-sm shadow-none";

const SIDE_SELECT =
  "t-select !rounded-[var(--t-event-pill-radius)] !bg-white w-full min-h-[40px] h-10 text-sm shadow-none appearance-none !px-4 !pr-10";

const SIDE_BTN =
  "!rounded-[var(--t-event-pill-radius)] border-[var(--t-border)] bg-white text-[var(--t-text)] shadow-none hover:bg-black/[0.03]";

const DATE_PICKER_TRIGGER =
  "!rounded-[var(--t-event-pill-radius)] !bg-white !min-h-[36px] !h-9 !py-0";

const PAID_AMOUNT_INPUT = `${SIDE_CONTROL} mt-0.5 !min-h-[36px] !h-9 cursor-text tabular-nums`;

const MENU_ITEM =
  "rounded-[var(--t-event-pill-radius)] px-3 py-2 text-left text-[10px] font-normal uppercase tracking-[0.16em]";

function sanitizeMoneyInput(raw) {
  const cleaned = String(raw ?? "").replace(/[^\d.]/g, "");
  const dot = cleaned.indexOf(".");
  if (dot === -1) return cleaned;
  return `${cleaned.slice(0, dot)}.${cleaned.slice(dot + 1).replace(/\./g, "")}`;
}

function paidAmountDisplay(value) {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
}

function newLineId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `line-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function seedLineItems(invoice) {
  const existing = Array.isArray(invoice?.lineItems) ? invoice.lineItems : [];
  const fallbackTitle = invoice?.serviceTitle || invoice?.type || "Services";
  const fallbackDescription = invoice?.serviceDescription || "";
  if (existing.length > 0) {
    return existing.map((item, index) => ({
      id: item.id || newLineId(),
      title: item.title || (index === 0 ? fallbackTitle : ""),
      description: item.description || (index === 0 ? fallbackDescription : ""),
      amount: paidAmountDisplay(item.amount),
    }));
  }
  return [
    {
      id: newLineId(),
      title: fallbackTitle,
      description: fallbackDescription,
      amount: paidAmountDisplay(invoice?.amount ?? ""),
    },
  ];
}

export default function InvoiceEditSheet({ invoice, open, onOpenChange, onSaved, onDeleted }) {
  const [issued, setIssued] = useState("");
  const [due, setDue] = useState("");
  const [paymentPlan, setPaymentPlan] = useState("full");
  const [deposits, setDeposits] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState("");
  const [openPaidDateDepId, setOpenPaidDateDepId] = useState(null);

  useEffect(() => {
    if (!invoice) return;
    const plan = normalizePaymentPlan(invoice.paymentPlan);
    const amt = Number(invoice.amount) || 0;
    const existing = (invoice.deposits || []).map((d) => ({
      ...d,
      paid_amount: d.paid_amount ?? 0,
    }));
    const seeded = seedLineItems(invoice);
    const lineTotal = roundMoney(
      seeded.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0),
    );

    setIssued((invoice.issued || "").slice(0, 10));
    setDue((invoice.due || invoice.issued || "").slice(0, 10));
    setPaymentPlan(plan);
    setLineItems(seeded);
    setDeposits(
      reconcileDepositSchedule(
        syncDepositsToPaymentPlan(
          existing,
          lineTotal > 0 ? lineTotal : amt,
          plan,
          invoice.issued,
          invoice.due,
        ),
        lineTotal > 0 ? lineTotal : amt,
      ),
    );
    setError("");
    setOpenPaidDateDepId(null);
    setConfirmDelete(false);
  }, [invoice]);

  useEffect(() => {
    if (!open) {
      setOpenPaidDateDepId(null);
      setConfirmDelete(false);
    }
  }, [open]);

  const lineTotal = useMemo(
    () => roundMoney(lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0)),
    [lineItems],
  );

  if (!invoice) return null;

  const isPaid = (invoice.status || "").toUpperCase() === "PAID";
  const partial = isPartialPlan(paymentPlan);
  const savedAmount = Number(invoice.amount) || 0;

  const updateLineItem = (id, field, value) => {
    setLineItems((prev) => {
      const next = prev.map((item) => (item.id === id ? { ...item, [field]: value } : item));
      if (field === "amount") {
        const total = roundMoney(
          next.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0),
        );
        if (total > 0) {
          setDeposits((prevDeps) => reconcileDepositSchedule(prevDeps, total));
        }
      }
      return next;
    });
  };

  const handleAddLineItem = () => {
    setLineItems((prev) => [
      ...prev,
      { id: newLineId(), title: "", description: "", amount: "" },
    ]);
  };

  const handleRemoveLineItem = (id) => {
    setLineItems((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((item) => item.id !== id);
      const total = roundMoney(
        next.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0),
      );
      if (total > 0) {
        setDeposits((prevDeps) => reconcileDepositSchedule(prevDeps, total));
      }
      return next;
    });
  };

  const updateDeposit = (id, field, value) => {
    setDeposits((prev) => {
      const next = prev.map((d) => (d.id === id ? { ...d, [field]: value } : d));
      if (field === "paid_amount" || field === "paid_at") {
        const total = lineTotal > 0 ? lineTotal : savedAmount;
        return reconcileDepositSchedule(next, total);
      }
      return next;
    });
  };

  const markDepositPaid = (dep) => {
    const today = new Date().toISOString().slice(0, 10);
    setDeposits((prev) => {
      const next = prev.map((d) =>
        d.id === dep.id
          ? {
              ...d,
              paid_amount: parseFloat(d.paid_amount) > 0 ? d.paid_amount : d.amount,
              paid_at: d.paid_at || today,
            }
          : d,
      );
      return reconcileDepositSchedule(next, lineTotal > 0 ? lineTotal : savedAmount);
    });
  };

  const handlePaymentPlanChange = (plan) => {
    const normalized = normalizePaymentPlan(plan);
    setPaymentPlan(normalized);
    setOpenPaidDateDepId(null);
    const total = lineTotal > 0 ? lineTotal : savedAmount;
    setDeposits((prev) =>
      reconcileDepositSchedule(
        syncDepositsToPaymentPlan(prev, total, normalized, invoice.issued, invoice.due),
        total,
      ),
    );
  };

  const handleAddPayment = () => {
    setOpenPaidDateDepId(null);
    setDeposits((prev) =>
      reconcileDepositSchedule(
        addPartialDeposit(prev, lineTotal || savedAmount, issued || invoice.issued),
        lineTotal || savedAmount,
      ),
    );
  };

  const handleRemovePayment = (depId) => {
    setOpenPaidDateDepId(null);
    setDeposits((prev) =>
      reconcileDepositSchedule(
        removePartialDeposit(prev, depId, lineTotal || savedAmount),
        lineTotal || savedAmount,
      ),
    );
  };

  const buildDepositPayload = () =>
    deposits.map((d, i) => {
      const row = {
        id: d.id,
        paid_amount: parseFloat(d.paid_amount) || 0,
        paid_at: d.paid_at || null,
      };
      if (partial) {
        row.label = d.label || `Payment ${i + 1}`;
        row.amount = parseFloat(d.amount) || 0;
        row.due = (d.due || due || issued || "").slice(0, 10);
      }
      return row;
    });

  const handleSave = async ({ close = true } = {}) => {
    if (isPaid) {
      if (close) onOpenChange(false);
      return true;
    }
    const cleanedLines = lineItems
      .map((item) => ({
        id: item.id,
        title: (item.title || "").trim(),
        description: (item.description || "").trim(),
        amount: parseFloat(item.amount) || 0,
      }))
      .filter((item) => item.title || item.amount > 0);

    if (cleanedLines.length === 0) {
      setError("Add at least one line item on the invoice.");
      return false;
    }
    for (const item of cleanedLines) {
      if (!item.title) {
        setError("Each line item needs a title.");
        return false;
      }
      if (!item.amount || item.amount <= 0) {
        setError("Each line item needs an amount greater than zero.");
        return false;
      }
    }
    if (!issued) {
      setError("Issued date is required.");
      return false;
    }

    setSaving(true);
    setError("");
    try {
      const data = await patchRevenueInvoice(invoice.id, {
        issued,
        due: due || issued,
        line_items: cleanedLines,
        payment_plan: paymentPlan,
        deposits: buildDepositPayload(),
      });
      toast.success(`${invoice.invoiceNumber} updated`);
      if (data?.promotion?.client_id) {
        toast.success("Prospect promoted to active engagement");
      }
      onSaved?.(data?.invoice);
      if (close) onOpenChange(false);
      return true;
    } catch (e) {
      setError(formatApiDetail(e.response?.data?.detail, "Could not save."));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDialogOpenChange = (next) => {
    if (next) {
      onOpenChange(true);
      return;
    }
    if (saving || deleting) return;
    if (isPaid) {
      onOpenChange(false);
      return;
    }
    void handleSave({ close: true });
  };

  const handleDelete = async () => {
    if (isPaid) return;
    const invoiceKey = invoice.id || invoice.invoiceNumber;
    if (!invoiceKey) return;
    setDeleting(true);
    setError("");
    try {
      await deleteRevenueInvoice(invoiceKey);
      toast.success(`Invoice ${invoice.invoiceNumber} deleted`);
      onDeleted?.(invoice);
      setConfirmDelete(false);
      onOpenChange(false);
    } catch (e) {
      setError(formatApiDetail(e.response?.data?.detail, "Could not delete invoice."));
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogContent
          className="max-w-[min(1120px,calc(100vw-1.5rem))] w-full h-[min(92vh,960px)] p-0 gap-0 overflow-hidden border-0 bg-transparent shadow-none sm:rounded-[var(--neo-radius,18px)] [&>button]:right-3 [&>button]:top-3 [&>button]:z-20 [&>button]:rounded-xl [&>button]:p-2 [&>button]:text-[var(--t-meta)] [&>button]:opacity-100 [&>button]:ring-offset-[var(--t-bg)] [&>button]:hover:bg-black/[0.04] [&>button]:hover:opacity-100"
          data-testid="invoice-edit-dialog"
        >
          <div
            className="glass-neo-lab relative flex h-full min-h-0 flex-col overflow-hidden rounded-[var(--neo-radius,18px)] border border-white/60 lg:flex-row"
            data-neo="merge"
          >
            <div className="glass-neo-lab__wash" aria-hidden />
            <div className="relative z-10 min-h-0 flex-1 overflow-y-auto bg-[var(--neo-well)] px-3 py-4 sm:px-5 sm:py-5">
              <DialogHeader className="sr-only">
                <DialogTitle>{isPaid ? "View" : "Edit"} {invoice.invoiceNumber}</DialogTitle>
                <DialogDescription>
                  {isPaid
                    ? `View invoice ${invoice.invoiceNumber} for ${invoice.client}.`
                    : `Edit invoice fields on the document preview for ${invoice.client}.`}
                </DialogDescription>
              </DialogHeader>
              <div
                className={`neo-panel mx-auto max-w-[680px] overflow-hidden p-0${
                  isPaid ? " pointer-events-none select-none" : ""
                }`}
                aria-readonly={isPaid || undefined}
              >
                <InvoiceDocumentEditor
                  invoiceNumber={invoice.invoiceNumber}
                  clientName={invoice.client}
                  issued={issued}
                  due={due}
                  onIssuedChange={setIssued}
                  onDueChange={setDue}
                  lineItems={lineItems}
                  onLineChange={updateLineItem}
                  onAddLine={handleAddLineItem}
                  onRemoveLine={handleRemoveLineItem}
                  paymentPlan={paymentPlan}
                  deposits={deposits}
                />
              </div>
            </div>

            <aside className="relative z-10 flex w-full shrink-0 flex-col border-t border-[var(--t-border)] bg-[var(--neo-sidebar)] backdrop-blur-xl lg:w-[300px] lg:border-l lg:border-t-0">
              <div className="border-b border-[var(--t-border)] px-4 py-4">
                <div className="label-eyebrow mb-2">{isPaid ? "Invoice" : "Edit invoice"}</div>
                <div className="font-heading text-xl tracking-tight !font-extralight">
                  {invoice.invoiceNumber}
                </div>
                <p className="mt-0.5 text-sm text-[var(--t-meta)]">{invoice.client}</p>
                <p className="mt-2 text-xs text-[var(--t-meta)]">
                  {isPaid
                    ? "Paid — download or email the PDF anytime."
                    : "Click fields on the invoice to edit. Save refreshes the PDF packet."}
                </p>
              </div>

              <div className="t-scroll-soft min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
                <div>
                  <Label className="label-eyebrow mb-2 block">Payment schedule</Label>
                  <Select
                    value={paymentPlan}
                    onValueChange={handlePaymentPlanChange}
                    disabled={isPaid}
                  >
                    <SelectTrigger className={SIDE_SELECT} data-testid="invoice-edit-plan">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[80] min-w-[12rem] neo-panel p-2 text-[var(--t-text)] t-glass">
                      <SelectItem value="full" className={MENU_ITEM}>
                        {PAYMENT_PLAN_LABELS.full}
                      </SelectItem>
                      <SelectItem value="partial" className={MENU_ITEM}>
                        {PAYMENT_PLAN_LABELS.partial}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <ul className="space-y-3" data-testid="invoice-edit-deposits">
                  {deposits.map((dep, index) => (
                    <li key={dep.id} className="neo-panel p-3">
                      <div className="mb-2 flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-heading !font-light">
                            {dep.label || `Payment ${index + 1}`}
                          </div>
                          <div className="text-xs text-[var(--t-meta)]">
                            Due {formatDisplayDate(dep.due)} · {formatUsd(dep.amount)}
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          {partial && deposits.length > 2 && !isPaid ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 rounded-[var(--t-event-pill-radius)] text-[var(--t-meta)] hover:bg-black/[0.04] hover:text-[var(--t-text)]"
                              onClick={() => handleRemovePayment(dep.id)}
                              aria-label={`Remove ${dep.label || `payment ${index + 1}`}`}
                              data-testid={`invoice-edit-remove-payment-${dep.id}`}
                            >
                              <Trash2 size={14} strokeWidth={1.4} />
                            </Button>
                          ) : null}
                          {!isPaid ? (
                            <button
                              type="button"
                              className="t-pill t-pill-accent inline-flex cursor-pointer border bg-transparent font-[inherit] hover:opacity-90"
                              onClick={() => markDepositPaid(dep)}
                            >
                              Mark paid
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="mb-1 block text-[10px] uppercase tracking-[0.12em] text-[var(--t-meta)]">
                            Paid amount
                          </Label>
                          <input
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            placeholder="0"
                            value={paidAmountDisplay(dep.paid_amount)}
                            onChange={(e) =>
                              updateDeposit(dep.id, "paid_amount", sanitizeMoneyInput(e.target.value))
                            }
                            onFocus={(e) => e.target.select()}
                            readOnly={isPaid}
                            disabled={isPaid}
                            className={PAID_AMOUNT_INPUT}
                            data-testid={`invoice-edit-paid-amount-${dep.id}`}
                          />
                        </div>
                        <div>
                          <Label className="mb-1 block text-[10px] uppercase tracking-[0.12em] text-[var(--t-meta)]">
                            Paid date
                          </Label>
                          <div>
                            <InlineDatePickerField
                              value={(dep.paid_at || "").slice(0, 10)}
                              onChange={(next) => updateDeposit(dep.id, "paid_at", next || null)}
                              placeholder="Select date"
                              allowClear
                              triggerClassName={DATE_PICKER_TRIGGER}
                              expanded={!isPaid && openPaidDateDepId === dep.id}
                              onExpandedChange={(next) =>
                                setOpenPaidDateDepId(!isPaid && next ? dep.id : null)
                              }
                              detachPanel
                              data-testid={`invoice-edit-paid-date-${dep.id}`}
                            />
                          </div>
                        </div>
                      </div>
                      {openPaidDateDepId === dep.id ? (
                        <InlineDatePickerPanel
                          value={(dep.paid_at || "").slice(0, 10)}
                          onChange={(next) => updateDeposit(dep.id, "paid_at", next || null)}
                          onClose={() => setOpenPaidDateDepId(null)}
                          allowClear
                          className="mt-2"
                          data-testid={`invoice-edit-paid-date-${dep.id}-panel`}
                        />
                      ) : null}
                    </li>
                  ))}
                </ul>

                {partial && !isPaid ? (
                  <Button
                    type="button"
                    variant="outline"
                    className={`w-full ${SIDE_BTN}`}
                    onClick={handleAddPayment}
                    data-testid="invoice-edit-add-payment"
                  >
                    <Plus size={16} strokeWidth={1.4} className="mr-2" />
                    Add payment
                  </Button>
                ) : null}

                {error ? <p className="text-sm text-[var(--t-error)]">{error}</p> : null}
              </div>

              <div className="space-y-3 border-t border-[var(--t-border)] px-4 py-4">
                {isPaid ? (
                  <p className="text-center text-xs text-[var(--t-meta)]" data-testid="invoice-edit-paid-locked">
                    Paid — editing locked. Download or email the PDF below.
                  </p>
                ) : (
                  <button
                    type="button"
                    className="t-btn-primary w-full disabled:opacity-40"
                    disabled={saving || deleting}
                    onClick={() => void handleSave()}
                    data-testid="invoice-edit-save"
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                )}
                {invoice.libraryFileId || invoice.id ? (
                  <PacketActions
                    fileId={invoice.libraryFileId}
                    invoiceId={invoice.id}
                    clientId={invoice.clientId}
                    className="w-full [&_button]:flex-1"
                    size="sm"
                  />
                ) : null}
                {!isPaid ? (
                  <button
                    type="button"
                    className="label-eyebrow inline-flex min-h-[40px] w-full items-center justify-center border-0 bg-transparent !px-0 !py-0 text-[var(--t-meta)] transition-colors hover:text-[var(--t-accent)] disabled:pointer-events-none disabled:opacity-40"
                    onClick={() => setConfirmDelete(true)}
                    disabled={saving || deleting}
                    data-testid="invoice-edit-delete"
                  >
                    Delete invoice
                  </button>
                ) : null}
              </div>
            </aside>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent
          className="glass-neo-lab z-[80] max-w-md gap-5 border border-white/60 p-6 text-[var(--t-text)] shadow-none sm:rounded-[var(--neo-radius-sm,14px)] neo-panel"
          data-neo="merge"
          data-testid="invoice-edit-delete-confirm"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl font-light tracking-tight text-[var(--t-text)]">
              Delete invoice?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm font-light text-[var(--t-meta)]">
              <span className="text-[var(--t-text)]">{invoice.invoiceNumber}</span> for{" "}
              <span className="text-[var(--t-text)]">{invoice.client}</span> will be removed from revenue
              tracking and the engagement library. You cannot undo this.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-3 sm:justify-end">
            <AlertDialogCancel
              className="t-btn-secondary min-h-[44px] border-[var(--t-border)] bg-[var(--t-bg)] text-[var(--t-text)] hover:opacity-90"
              disabled={deleting}
            >
              Cancel
            </AlertDialogCancel>
            <button
              type="button"
              className="t-btn-primary min-h-[44px] bg-[var(--t-danger,#b42318)] hover:opacity-90 disabled:opacity-40"
              onClick={handleDelete}
              disabled={deleting}
              data-testid="invoice-edit-delete-confirm"
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
